import React from "react";
import { Link } from "gatsby";
import classNames from "classnames";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import classnames from "classnames";

import { Loading } from "./Loading";

type Color = "primary" | "secondary";
type Size = "large" | "medium" | "small";

type ButtonClassesProps = {
  color?: Color;
  size?: Size;
  plain?: boolean;
  outlined?: boolean;
  disabled?: boolean;
  shadow?: boolean;
};

const buttonClasses = ({
  color,
  size,
  plain,
  outlined,
  disabled,
  shadow,
}: ButtonClassesProps) => {
  const sizeClasses = {
    "h-12 text-lg px-6": size === "large",
    "h-10 text-base px-5": !size || size == "medium",
    "h-8 text-sm px-4": size == "small",
  };

  const standardClasses = {
    "text-white bg-blue-400 hover:bg-blue-500": !color || color === "primary",
    "text-blue-500 bg-yellow-500 hover:bg-yellow-400": color === "secondary",
  };

  const outlinedClasses = {
    "font-bold text-blue-500 hover:text-white bg-white hover:bg-blue-500 border border-blue-500 hover:border-blue-500":
      !color || (color === "primary" && outlined),
    "text-yellow-500 hover:text-white hover:bg-yellow-500 border border-yellow-500 hover:border-yellow-500":
      color === "secondary" && outlined,
  };

  const plainClasses = {
    "text-blue-500 hover:text-blue-400 hover:underline":
      !color || color === "primary",
    "text-yellow-500 hover:text-yellow-500 hover:underline":
      color === "secondary",
  };

  const disabledClasses = {
    "cursor-pointer": !disabled,
    "opacity-50 cursor-default hover:shadow-none hover:bg-current": disabled,
  };

  const shadowClasses = {
    "shadow-md hover:shadow-lg hover:scale-101": shadow,
  };

  let buttonClasses = { ...sizeClasses, ...disabledClasses };

  if (!plain && !outlined) {
    buttonClasses = { ...buttonClasses, ...standardClasses };
  }

  if (plain) {
    buttonClasses = { ...buttonClasses, ...plainClasses };
  }

  if (outlined) {
    buttonClasses = { ...buttonClasses, ...outlinedClasses };
  }

  if (shadow) {
    buttonClasses = { ...buttonClasses, ...shadowClasses };
  }

  return classNames({
    "flex justify-center items-center font-medium rounded-md transform transition duration-200":
      true,
    ...buttonClasses,
  });
};

type ButtonProps = {
  children: React.ReactNode;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
  loading?: boolean;
  onClick?: (
    event?: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<void> | void;
} & ButtonClassesProps;

export const Button = (props: ButtonProps) => {
  const {
    type = "button",
    children,
    color,
    size,
    onClick,
    plain,
    outlined,
    shadow,
    disabled,
    loading,
  } = props;

  const spinnerClass = classnames("absolute", !loading && "invisible");
  return (
    <button
      type={type}
      onClick={onClick}
      className={buttonClasses({
        color,
        size,
        plain,
        outlined,
        disabled,
        shadow,
      })}
      {...props}
    >
      <div className={classnames(loading && "invisible")}>{children}</div>
      <Loading className={spinnerClass} />
    </button>
  );
};

type LinkButtonProps = {
  children?;
  to?;
  onClick?;
  props?;
  arrow?: boolean;
  color?: Color;
  size?: Size;
  plain?: boolean;
  outlined?: boolean;
  disabled?: boolean;
  shadow?: boolean;
};

export const LinkButton = ({
  children,
  color,
  size,
  to,
  onClick,
  plain,
  outlined,
  shadow,
  arrow,
  disabled,
  props,
}: LinkButtonProps) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={buttonClasses({
        color,
        size,
        plain,
        outlined,
        disabled,
        shadow,
      })}
      {...props}
    >
      {children} {arrow && <ArrowRightIcon className="h-4 ml-2" />}
    </Link>
  );
};
